import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSnackbar } from "@/hooks/useSnackbar";
import {
  GROUP_VISIT_DETAIL_QUERY_KEY,
  useGroupVisitById,
} from "@/queries/group-visit/use-group-visit-by-id";
import { GROUP_VISIT_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-visit-list";
import { AUDIT_LOG_LIST_QUERY_KEY } from "@/queries/practice-setting/audit-log/use-audit-log-list";
import { useGroupVisitTherapists } from "@/queries/group-visit/use-group-visit-therapists";
import { useUpdateGroupVisit } from "@/queries/group-visit/use-update-group-visit";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import {
  GroupTherapist,
  GroupTherapistRow,
  ProviderAssignmentValues,
} from "../group-visit-types";
import { toGroupTherapist } from "../group-visit-utils";

export type UseGroupTherapists = {
  primaryTherapist?: GroupTherapist;
  coTherapists: GroupTherapist[];
  /** Current assignment, for the Provider Assignment modal. */
  assignment: ProviderAssignmentValues;
  isLoading: boolean;
  saveAssignment: (values: ProviderAssignmentValues) => Promise<void>;
};

/**
 * A group's assigned providers and the details behind them.
 *
 * @author Rajkumar Rathod
 */
export const useGroupTherapists = (
  groupVisitId?: string
): UseGroupTherapists => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const { data: savedGroup } = useGroupVisitById(groupVisitId);
  const { mutateAsync: updateGroupVisit } = useUpdateGroupVisit();

  const providerIds = useMemo(
    () =>
      [savedGroup?.primaryTherapist, savedGroup?.coTherapist].filter(
        (id): id is string => !!id
      ),
    [savedGroup]
  );

  const { data: therapists, isLoading } =
    useGroupVisitTherapists<GroupTherapistRow[]>(providerIds);

  const byId = useMemo(() => {
    const rows = therapists ?? [];
    return new Map(rows.map((row) => [row.uuid, row]));
  }, [therapists]);

  const primaryTherapist = useMemo(() => {
    const row = savedGroup?.primaryTherapist
      ? byId.get(savedGroup.primaryTherapist)
      : undefined;

    if (row) return toGroupTherapist(row);
    if (!savedGroup?.primaryTherapist) return undefined;

    // Details may not have loaded yet; the name on the group still reads.
    return toGroupTherapist({
      uuid: savedGroup.primaryTherapist,
      name: savedGroup.primaryTherapistName,
    });
  }, [savedGroup, byId]);

  const coTherapists = useMemo(() => {
    if (!savedGroup?.coTherapist) return [];
    const row = byId.get(savedGroup.coTherapist);

    return [
      row
        ? toGroupTherapist(row)
        : toGroupTherapist({
            uuid: savedGroup.coTherapist,
            name: savedGroup.coTherapistName,
          }),
    ];
  }, [savedGroup, byId]);

  const assignment = useMemo<ProviderAssignmentValues>(
    () => ({
      primaryTherapist: savedGroup?.primaryTherapist
        ? {
            value: savedGroup.primaryTherapist,
            label: savedGroup.primaryTherapistName ?? "",
          }
        : null,
      coTherapist: savedGroup?.coTherapist
        ? {
            value: savedGroup.coTherapist,
            label: savedGroup.coTherapistName ?? "",
          }
        : null,
    }),
    [savedGroup]
  );

  // The update rewrites every field, so the saved record travels with the edit.
  const saveAssignment = async (values: ProviderAssignmentValues) => {
    if (!savedGroup) return;

    try {
      const res = await updateGroupVisit({
        groupVisit: {
          ...savedGroup,
          primaryTherapist: values.primaryTherapist?.value ?? undefined,
          coTherapist: values.coTherapist?.value ?? undefined,
        },
      });

      queryClient.invalidateQueries({
        queryKey: [GROUP_VISIT_DETAIL_QUERY_KEY, groupVisitId],
      });
      queryClient.invalidateQueries({ queryKey: [GROUP_VISIT_LIST_QUERY_KEY] });
      /** Refreshes the Therapist change history panel with the new entry. */
      queryClient.invalidateQueries({ queryKey: [AUDIT_LOG_LIST_QUERY_KEY] });
      showSnackbar(getSuccessSnackbar(res));
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
      throw error;
    }
  };

  return {
    primaryTherapist,
    coTherapists,
    assignment,
    isLoading,
    saveAssignment,
  };
};
